import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SmartShell } from '../../components/smart/SmartShell';
import { Button } from '../../components/smart/ui/Button';
import { SMART_ROUTES } from '../../constants';
import { smartMockOperationId } from '../../gallery/smartMock';

export const SendDonePage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const checkRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const replayCheckAnimation = useCallback(() => {
    const check = checkRef.current;
    const trigger = triggerRef.current;
    if (!check || !trigger) return;

    const path = check.querySelector('svg path');
    if (path instanceof SVGPathElement) {
      const len = Math.ceil(path.getTotalLength());
      path.style.strokeDasharray = String(len);
      path.style.strokeDashoffset = String(len);
    }

    check.setAttribute('data-state', 'out');
    trigger.setAttribute('data-state', 'out');
    void check.offsetWidth;
    check.setAttribute('data-state', 'in');
    trigger.setAttribute('data-state', 'in');
  }, []);

  useEffect(() => {
    replayCheckAnimation();
  }, [replayCheckAnimation]);

  return (
    <SmartShell hideHeader>
      <section className="border-border bg-bg-elevated rounded-3xl border p-6 text-center">
        <button
          ref={triggerRef}
          type="button"
          onClick={replayCheckAnimation}
          className="t-success-check-trigger bg-accent/15 relative mx-auto flex h-16 w-16 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-90 active:scale-95"
          data-state="out"
          aria-label={t('wizard.done.send.replayCheck')}
          title={t('wizard.done.send.replayCheck')}
        >
          <span aria-hidden className="t-success-check-ring" />
          <span
            ref={checkRef}
            aria-hidden
            className="t-success-check text-accent"
            data-state="out"
          >
            <svg viewBox="0 0 48 48" fill="none" className="h-8 w-8" aria-hidden>
              <path
                d="M14 24 L22 32 L34 16"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
        <h2 className="mt-4 text-2xl font-semibold tracking-tight">
          {t('wizard.done.send.title')}
        </h2>
        <p className="text-fg-muted mx-auto mt-2 max-w-xs text-sm">
          {t('wizard.done.send.description')}
        </p>
        <div className="border-border bg-bg/40 mx-auto mt-5 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-mono">
          <span className="text-fg-muted">{t('wizard.done.send.operation')}</span>
          <span className="text-fg">{smartMockOperationId}</span>
        </div>
        <div className="mt-6">
          <Button
            onClick={() => navigate(SMART_ROUTES.HOME)}
            fullWidth
            size="lg"
          >
            {t('wizard.done.send.home')}
          </Button>
        </div>
      </section>
    </SmartShell>
  );
};
